import { useState } from "react";
import { downloadMyData, requestErasure } from "./api.js";
import { Download, Notice } from "./ui.js";

/**
 * Download my data, request erasure. NDPA 2023 rights, self-service — §14 requires data-subject
 * access and erasure tooling, and giving the applicant the route themselves is both kinder and far
 * less work than handling every request by hand.
 *
 * Erasure asks for a confirmation step rather than firing on the first click, and says plainly that
 * a person reviews it. Neither is friction for its own sake: the answer is sometimes "no, this
 * record is retained", and finding that out *after* clicking a button labelled "Delete everything"
 * is worse than being told first.
 */
export const MyData = (): JSX.Element => {
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const response = await requestErasure(note);
      setResult(response.message);
      setConfirming(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That request could not be sent.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1 className="font-heading text-2xl font-semibold">My data</h1>
      <p className="sga-muted mt-2 max-w-2xl text-sm">
        Two rights the Nigeria Data Protection Act 2023 gives you, without having to ask anyone for
        them.
      </p>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <section className="sga-card flex flex-col p-5">
          <h2 className="font-heading text-base font-semibold">Download my data</h2>
          <p className="sga-muted mt-2 flex-1 text-sm">
            Everything we hold about you — your details, their change history, and every application
            you have submitted — as a single file. Encrypted answers are included in full: they are
            yours.
          </p>
          <div className="mt-4">
            <button type="button" onClick={downloadMyData} className="sga-btn sga-btn--primary">
              <Download />
              Download
            </button>
          </div>
        </section>

        <section className="sga-card flex flex-col p-5">
          <h2 className="font-heading text-base font-semibold">Request erasure</h2>
          <p className="sga-muted mt-2 flex-1 text-sm">
            You can ask us to delete your data. A person reviews every request — some records must
            be kept for a period after a programme closes, and we will tell you if that applies to
            you. We respond within 30 days.
          </p>

          <div className="mt-4">
            {result ? (
              <Notice>{result}</Notice>
            ) : confirming ? (
              <div>
                <label htmlFor="erasure-note" className="sga-label">
                  Anything you would like us to know (optional)
                </label>
                <textarea
                  id="erasure-note"
                  rows={3}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  className="sga-input mt-1.5"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="sga-btn sga-btn--secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void submit()}
                    className="sga-btn sga-btn--danger"
                  >
                    {busy ? "Sending…" : "Send erasure request"}
                  </button>
                </div>
                {error && (
                  <div className="mt-3">
                    <Notice tone="error">{error}</Notice>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="sga-btn sga-btn--danger"
              >
                Request erasure
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};
